import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})

export class TileService {
  public userCanSeeTile(tileType: TileTypes, userRoles: string[]) {
    const tileSecurity = TileInformation.find(ts => ts.type === tileType);
    if (!tileSecurity.isEnabled) {
      return false;
    }

    if (tileSecurity.exclude) {
      return !userRoles.some(r => tileSecurity.roles.includes(r));
    } else {
      return userRoles.some(r => tileSecurity.roles.includes(r)) || !tileSecurity.roles.length;
    }
  }

  public getUserAccessibleTiles(userRoles: string[]): ITileInfo[] {
    const accessibleTiles = [];
    Object.keys(TileTypes).forEach(tileType => {
      if (this.userCanSeeTile(tileType as TileTypes, userRoles)) {
        accessibleTiles.push(TileInformation.find(ti => ti.type === tileType));
      }
    });

    return accessibleTiles;
  }

  public getTileTitle(tileType: TileTypes): string {
    return TileInformation.find(ti => ti.type === tileType).title;
  }
}

export enum TileTypes {
  CreditApprovalBacklog = 'CreditApprovalBacklog',
  CreditLinks = 'CreditLinks',
  CreditMetrics = 'CreditMetrics',
  YOYCreditMetrics = 'YOYCreditMetrics',
  FinalFinisherGaps = 'FinalFinisherGaps',
  MyCreditApprovalAndPOAcks = 'MyCreditApprovalAndPOAcks',
  PlannedShipmentCommitted = 'PlannedShipmentCommitted',
  SpaExpiringPlannedShipmentCommitted = 'SpaExpiringPlannedShipmentCommitted',
  PlannedShipmentUpcomingReleased = 'PlannedShipmentUpcomingReleased',
  WorkPackageValidationRequests = 'WorkPackageValidationRequests',
  WorkPackageApprovalRequests = 'WorkPackageApprovalRequests',
  WorkPackageTransitionRequests = 'WorkPackageTransitionRequests',
  WorkPackageTransmitRequests = 'WorkPackageTransmitRequests',
  RevenueOutOfBalance = 'RevenueOutOfBalance',
  AccountsPayableThreeWayMatch = 'AccountsPayableThreeWayMatch',
  ReturnReasonCounts = 'ReturnReasonCounts',
}

export interface ITileInfo {
  type: TileTypes; // Tile type for security entry
  title: string; // The title for the tile
  info: string; // Info text for the tile
  roles: string[]; // Roles associated to tile type
  exclude: boolean; // Should roles exclude access to the tile?  Or permit access to the tile?
  hasHeaderExport: boolean;  // Is there a header level export for the tile?
  isEnabled: boolean; // Is the tile enabled?
}

const creditApproverRole = 'tsmt-credit-approver';

export const TileInformation: ITileInfo[] = [
  {
    type: TileTypes.CreditApprovalBacklog,
    title: 'Credit Approval Backlog',
    info: 'Credit Users an age summary of their New Open requests for all offices in your office selector. Data was last refreshed above, click to refresh as needed.',
    roles: [creditApproverRole],
    exclude: false,
    hasHeaderExport: true,
    isEnabled: true,
  },
  {
    type: TileTypes.CreditMetrics,
    title: 'Team Metrics/MDI',
    info: 'Credit Users metrics about their work for all offices in your office selector. Data is refreshed once daily.',
    roles: [creditApproverRole],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.YOYCreditMetrics,
    title: 'Team YOY Metrics',
    info: 'Credit Users metrics about their work for all offices in your office selector. Data is refreshed once daily.',
    roles: [creditApproverRole],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.CreditLinks,
    title: 'Credit Links',
    info: 'Links to frequently used Credit Team sites.',
    roles: [creditApproverRole],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.MyCreditApprovalAndPOAcks,
    title: 'My Credit Approval And POAcks',
    info: 'A listing of Credit Requests submitted by you that not completed based on the Credit and/or Acknowledgement status. These requests need action taken to be completed. The list includes all offices in your office selector. Data was last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.FinalFinisherGaps,
    title: 'Final Finisher Estimated Ship Dates',
    info: 'A listing of Final Finisher backlog data for the office location selected. Contact the Final Finisher to obtain FF ESD and/or actual FF actual shipment information and forward to appropriate mfg location for entry in the system. Data was last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.PlannedShipmentCommitted,
    title: 'Committed Planned Shipments (based on Product Release Date)',
    info: 'A listing of backlog data for the office location selected. Review the data to ensure project timelines are accurate. Data was last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.SpaExpiringPlannedShipmentCommitted,
    title: 'SPA Expiring Committed Planned Shipments',
    info: 'A listing of SPA (Special Pricing Auth) Expirations on backlog committed planned shipments for the office selected. Review the latest requested delivery window to ensure SPA Ship Qtr can be held. If SPA Ship Qtrs expire, the Bid will need to be re-coordinated which could result in different authorized levels and price control therefore will require price increases for the customer. Data was last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.PlannedShipmentUpcomingReleased,
    title: 'Upcoming Released Planned Shipments (based on Estimated Ship Date)',
    info: 'A listing of released data for office location selected. Data was last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.WorkPackageValidationRequests,
    title: 'Work Package Validation Requests',
    info: 'A listing of estimates you\'ve submitted for approval for all offices in your office selector. Data was last refreshed above, click to refresh as needed.',
    roles: ['tsmt-estimate-approver', 'tsmt-estimate-fulfillment'],
    exclude: true,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.WorkPackageApprovalRequests,
    title: 'Work Package Approval Requests',
    info: 'A listing of estimates submitted for your approval for all offices in your office selector. Data was last refreshed above, click to refresh as needed.',
    roles: ['tsmt-estimate-approver'],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.WorkPackageTransitionRequests,
    title: 'My Transitioned Work Packages',
    info: 'A listing of your Work Package Transmit Requests. The data is live and last refreshed above, click to refresh as needed.',
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.WorkPackageTransmitRequests,
    title: 'Work Package Transmit/Submit Requests',
    info: 'A listing of Work Package Transmit Requests for all offices in your office selector. The data is live and last refreshed above, click to refresh as needed.',
    roles: ['tsmt-estimate-fulfillment'],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.RevenueOutOfBalance,
    title: 'Revenue Out Of Balance Projects',
    info: `A listing of all projects where the revenue is out of balance between WorkSight and Oracle Financials for the office location selected.
          The Project Manager should review the scope modifications for completeness then submit to financials. Data was last refreshed above, click to refresh as needed.
          The data may take a moment to load, please be patient.`,
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: true,
  },
  {
    type: TileTypes.AccountsPayableThreeWayMatch,
    title: 'Accounts Payable 3-Way Match',
    info: `A listing of project purchase orders set up as 3-way match needing review.
    Please review supporting documentation and either complete the receipt process
    if no issues exist, or follow up with the office for additional information required
    before the receipt process can be completed. This list is updated once per day with
    new/received activity.`,
    roles: [],
    exclude: false,
    hasHeaderExport: false,
    isEnabled: environment.enableAp3WayMatchTile,
  },
  {
    type: TileTypes.ReturnReasonCounts,
    title: 'Return Reason Counts',
    info: `Displays a count of Return Reasons for the given timeframe. Clicking the
     export icon will give an export option that will include all requests for that
     time period with return reasons flagged. Data is refreshed once daily.`,
    roles: [creditApproverRole],
    exclude: false,
    hasHeaderExport: true,
    isEnabled: true,
  },
];
